import { GraphQL, useRpiGraphqlRequest } from "@rpi/openapi-api";
import {
  filterByMatchingAttributes,
  findAvailableAttributeCombinations,
  serializeCombination,
} from "../../hooks/useStandardProducts";
import { ATTRIBUTES } from "./attributeHelpers";
import { ChoiceStep } from "./ChoiceStep";
import { attributesForFiltering, StepProps } from "./ProductCreateNavigator";
import { getDisplayName, getProductImage } from "./attributeValueMappings";
import { ProductImage } from "./ProductImage";
import { TextCard } from "./TextCard";
import { useState } from "react";

export function PaperTypeStep(props: StepProps) {
  const paperKeys = props.attributeKeys;
  const [isCustomerProductCheckLoading, setIsCustomerProductCheckLoading] = useState(false);

  // Pairs reachable from the current upstream selections — the enabled set. Pairs outside it are shown disabled.
  const upstreamProducts = filterByMatchingAttributes(props.standardProducts, attributesForFiltering(props.state));
  const availablePairIds = new Set(
    findAvailableAttributeCombinations(upstreamProducts, paperKeys).map((combination) =>
      serializeCombination(paperKeys, combination)
    )
  );

  // Resolve the product from the full selection (including the chosen paper) for the duplicate check.
  const selectedProducts = filterByMatchingAttributes(props.standardProducts, props.state.attributes);
  const openapiProductId = selectedProducts.length === 1 ? selectedProducts[0].id : undefined;

  const request = useRpiGraphqlRequest<GraphQL.CustomerProductsQuery, GraphQL.CustomerProductsQueryVariables>(
    GraphQL.CustomerProductsDocument
  );
  const validateOpenapiSkuUnused = async () => {
    if (!openapiProductId) {
      return;
    }

    try {
      setIsCustomerProductCheckLoading(true);
      // Check that the customer hasn't already used this openapi product before advancing to the final step
      // This provides a nicer UX than the error they would get on trying to save the product
      const data = await request({
        limit: 1,
        offset: 0,
        search: {
          inputs: [
            {
              field: GraphQL.CustomerProductSearchField.OpenapiProductId,
              condition: GraphQL.SearchCondition.Equals,
              firstValue: openapiProductId?.toString(),
            },
          ],
        },
      });
      if (data.customerProducts.data.length > 0) {
        return (
          <>
            You already have a product with this exact combination of Cover, Finish, Orientation, Size, Bind, and Paper.
            <br />
            <br />
            Please go back to the previous steps to change one of these selections.
          </>
        );
      }
    } finally {
      setIsCustomerProductCheckLoading(false);
    }
  };

  return (
    <ChoiceStep
      {...props}
      // Show every (weight, style) pair in the catalog; pairs that exist in no product are never enumerated and stay hidden.
      getFilterAttributes={() => ({})}
      sortCombinations={(combinations) =>
        [...combinations].sort((a, b) => {
          const aDisabled = !availablePairIds.has(serializeCombination(paperKeys, a));
          const bDisabled = !availablePairIds.has(serializeCombination(paperKeys, b));
          if (aDisabled === bDisabled) return 0;
          return aDisabled ? 1 : -1;
        })
      }
      renderCard={(combination, id) => {
        const weightName = getDisplayName(ATTRIBUTES.TEXT_STOCK, String(combination[ATTRIBUTES.TEXT_STOCK]));
        const styleName = getDisplayName(ATTRIBUTES.STOCK_STYLE, String(combination[ATTRIBUTES.STOCK_STYLE]));
        if (!weightName || !styleName) return null;

        const disabled = !availablePairIds.has(id);
        const renderTooltip = disabled
          ? () => <>This paper type is not available in the bleed style you’ve selected.</>
          : undefined;

        return (
          <TextCard
            key={id}
            value={id}
            displayName={`${weightName} ${styleName}`}
            disabled={disabled}
            renderTooltip={renderTooltip}
          />
        );
      }}
      renderImage={(combination) => {
        const weightName = getDisplayName(ATTRIBUTES.TEXT_STOCK, String(combination[ATTRIBUTES.TEXT_STOCK]));
        const styleName = getDisplayName(ATTRIBUTES.STOCK_STYLE, String(combination[ATTRIBUTES.STOCK_STYLE]));
        const image = getProductImage(ATTRIBUTES.STOCK_STYLE, String(combination[ATTRIBUTES.STOCK_STYLE]));
        return image && weightName && styleName ? (
          <ProductImage displayName={`${weightName} ${styleName}`} image={image} />
        ) : null;
      }}
      flexDirection="column"
      onAdditionalValidation={validateOpenapiSkuUnused}
      isLoading={isCustomerProductCheckLoading}
    />
  );
}
